import React, { useState, useEffect, useRef, useMemo } from 'react';

const RelationshipField = ({ 
  label, 
  value, 
  onChange, 
  cards = [], 
  placeholder = "Search for a person...",
  multiple = false 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Filter cards based on search term
  const filteredCards = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const search = searchTerm.toLowerCase().trim();
    return cards.filter(card => 
      !card.deleted && // Exclude deleted cards
      (
        (card.firstName?.toLowerCase().includes(search)) ||
        (card.lastName?.toLowerCase().includes(search)) ||
        (`${card.firstName} ${card.lastName}`.toLowerCase().includes(search))
      )
    );
  }, [cards, searchTerm]);

  const handleSelect = (card) => {
    const selectedPerson = {
      cardId: card.id,
      name: `${card.firstName} ${card.lastName}`,
      group: card.groups?.[0] || ''
    };

    if (multiple) {
      onChange(value ? [...value, selectedPerson] : [selectedPerson]);
    } else {
      onChange(selectedPerson);
    }
    
    setSearchTerm('');
    setShowSuggestions(false);
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={() => setShowSuggestions(true)}
        className="w-full p-2 border rounded"
        placeholder={placeholder}
      />
      
      {/* Show selected values */}
      {value && (
        <div className="mt-2">
          {multiple ? (
            <div className="flex flex-wrap gap-2">
              {value.map(person => (
                <div key={person.cardId} className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                  <span>{person.name}</span>
                  <button
                    onClick={() => onChange(value.filter(p => p.cardId !== person.cardId))}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded inline-block">
              <span>{value.name}</span>
              <button
                onClick={() => onChange(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
          )}
        </div>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && filteredCards.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-md border">
          {filteredCards.map(card => (
            <button
              key={card.id}
              onClick={() => handleSelect(card)}
              className="w-full text-left px-4 py-2 hover:bg-gray-100"
            >
              {card.firstName} {card.lastName}
              {card.groups?.[0] && (
                <span className="text-gray-500 text-sm ml-2">
                  ({card.groups[0]})
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default RelationshipField; 